import { z } from 'zod';
import { workflowDefinitionStateSchema, workflowCatalogSelectionSchema } from './workflow-admission';

// ============================================================================
// Task #3694 — DTO risposta condivisi per gli endpoint workflow/campagne CRM
// consumati da packages/frontend/src/pages/crm/CRMDesktop.tsx.
//
// Convenzioni wire (stesse di schemas/crm.ts, sezione DTO risposta):
// - COUNT/SUM pg senza ::int → z.union([z.string(), z.number()]) (numeric = stringa sul wire)
// - timestamp/timestamptz → z.union([z.string(), z.date()]) (wire = ISO string; Date pre-serializzazione BE)
// - colonne NULLABLE / alias da LEFT JOIN → .nullable()
// - jsonb generici → z.unknown()
// - SELECT alias.* → colonne note dallo schema DB + .passthrough()
// ============================================================================

// Timestamp al boundary: ISO string sul wire, Date lato service (driver pg).
const wireTimestamp = z.union([z.string(), z.date()]);
// COUNT/aggregati pg senza cast ::int: stringa sul wire (numeric/bigint), talvolta number.
const pgCount = z.union([z.string(), z.number()]);

// ----------------------------------------------------------------------------
// 1) GET /crm/campaigns/:id/workflow-runs/stats → { success, stats }
//    Riga da crm-campaigns.routes.ts:1428-1457.
// ----------------------------------------------------------------------------
export const crmCampaignWorkflowStatSchema = z.object({
  trigger_id: z.string(), // cwt.id (uuid)
  workflow_id: z.string(), // cwt.workflow_id (uuid)
  workflow_name: z.string(), // w.name (INNER JOIN → normalmente presente)
  trigger_type: z.string(), // w.trigger_type
  trigger_config: z.unknown().nullable(), // w.trigger_config (jsonb, nullable in DB)
  is_active: z.boolean(), // cwt.is_active
  nodes: z.unknown().nullable(), // w.nodes (jsonb array, nullable sul wire)
  // COUNT(...) senza ::int → stringa sul wire.
  total_runs: pgCount, // COUNT(wr.id)
  success_count: pgCount, // COUNT(...) FILTER completed
  failed_count: pgCount, // COUNT(...) FILTER failed
  running_count: pgCount, // COUNT(...) FILTER running
  last_run_at: wireTimestamp.nullable(), // MAX(wr.started_at) → null se nessun run
  last_status: z.string().nullable(), // subquery correlata → null se nessun run
});
export type CrmCampaignWorkflowStat = z.infer<typeof crmCampaignWorkflowStatSchema>;

export const crmCampaignWorkflowStatsResponseSchema = z.object({
  success: z.boolean(),
  stats: z.array(crmCampaignWorkflowStatSchema),
});
export type CrmCampaignWorkflowStatsResponse = z.infer<typeof crmCampaignWorkflowStatsResponseSchema>;

// ----------------------------------------------------------------------------
// 2) GET /crm/campaigns/:id/workflow-runs → { success, runs, limit, offset }
//    Riga da crm-campaigns.routes.ts:1474-1487.
// ----------------------------------------------------------------------------
export const crmCampaignWorkflowRunSchema = z.object({
  id: z.string(), // wr.id (uuid)
  status: z.string(), // wr.status
  started_at: wireTimestamp, // wr.started_at (ISO string)
  completed_at: wireTimestamp.nullable(), // wr.completed_at (null se in corso)
  error_message: z.string().nullable(), // wr.error_message
  trigger_data: z.unknown().nullable(), // wr.trigger_data (jsonb)
  node_logs: z.unknown().nullable(), // wr.node_logs (jsonb)
  workflow_name: z.string(), // w.name
  workflow_id: z.string(), // w.id (uuid)
  trigger_type: z.string(), // w.trigger_type
});
export type CrmCampaignWorkflowRun = z.infer<typeof crmCampaignWorkflowRunSchema>;

export const crmCampaignWorkflowRunsResponseSchema = z.object({
  success: z.boolean(),
  runs: z.array(crmCampaignWorkflowRunSchema),
  limit: z.number(), // JS number lato handler
  offset: z.number(), // JS number lato handler
});
export type CrmCampaignWorkflowRunsResponse = z.infer<typeof crmCampaignWorkflowRunsResponseSchema>;

// ----------------------------------------------------------------------------
// 3) GET /crm/campaigns/all/workflow-runs → { success, runs }
//    Variante della riga base (crm-campaigns.routes.ts:310-337) con
//    triggered_by/campaign_id/campaign_name/trigger_config; NON ritorna limit/offset.
// ----------------------------------------------------------------------------
export const crmAllWorkflowRunSchema = crmCampaignWorkflowRunSchema.extend({
  triggered_by: z.string().nullable(), // wr.triggered_by (uuid, nullable)
  trigger_config: z.unknown().nullable(), // w.trigger_config (jsonb)
  campaign_id: z.string().nullable(),
  campaign_name: z.string().nullable(),
  campaign_link_id: z.string().nullable(),
  campaign_generation: z.number().nullable(),
  definition_version: pgCount.nullable(),
  provenance: z.enum(['unknown', 'recorded']),
});
export type CrmAllWorkflowRun = z.infer<typeof crmAllWorkflowRunSchema>;

export const crmAllWorkflowRunsResponseSchema = z.object({
  success: z.boolean(),
  runs: z.array(crmAllWorkflowRunSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});
export type CrmAllWorkflowRunsResponse = z.infer<typeof crmAllWorkflowRunsResponseSchema>;

// ----------------------------------------------------------------------------
// 4) GET /crm/campaigns/all/workflow-links → { success, links }
//    Riga da crm-campaigns.routes.ts:271-295.
// ----------------------------------------------------------------------------
export const crmCampaignWorkflowLinkSchema = z.object({
  trigger_id: z.string(), // cwt.id (uuid)
  campaign_id: z.string(), // cwt.campaign_id (uuid)
  workflow_id: z.string(), // cwt.workflow_id (uuid)
  is_active: z.boolean(), // cwt.is_active
  created_at: wireTimestamp, // cwt.created_at (ISO string)
  campaign_name: z.string(), // c.name
  campaign_type: z.string(), // c.type
  campaign_status: z.string(), // c.status
  workflow_name: z.string(), // w.name
  trigger_type: z.string(), // w.trigger_type
  trigger_config: z.unknown().nullable(), // w.trigger_config (jsonb)
  workflow_is_active: z.boolean(), // w.is_active
  nodes: z.unknown().nullable(), // w.nodes (jsonb array)
  // COUNT(...) subquery senza ::int → stringa sul wire.
  total_runs: pgCount,
  success_count: pgCount,
  failed_count: pgCount,
  last_run_at: wireTimestamp.nullable(), // MAX(wr.started_at) → null se nessun run
});
export type CrmCampaignWorkflowLink = z.infer<typeof crmCampaignWorkflowLinkSchema>;

export const crmCampaignWorkflowLinksResponseSchema = z.object({
  success: z.boolean(),
  links: z.array(crmCampaignWorkflowLinkSchema),
});
export type CrmCampaignWorkflowLinksResponse = z.infer<typeof crmCampaignWorkflowLinksResponseSchema>;

// ----------------------------------------------------------------------------
// 5) GET /workflows?limit=200 → { workflows, total, limit, offset } (BARE, no success)
//    Proiezione SELECT w.* (workflow.routes.ts:158-166): enumero le colonne
//    fisiche della tabella public.workflows (schema.sql:23392) + alias calcolati.
//    .passthrough() per tollerare eventuali colonne future su w.*.
// ----------------------------------------------------------------------------
export const workflowListItemSchema = z
  .object({
    // Colonne fisiche public.workflows (w.*)
    id: z.string(), // uuid
    tenant_id: z.string(), // uuid
    name: z.string(),
    description: z.string().nullable(), // text NULLABLE
    trigger_type: z.string(),
    trigger_config: z.unknown(), // jsonb NOT NULL (default {})
    nodes: z.unknown(), // jsonb NOT NULL (default [])
    edges: z.unknown(), // jsonb NOT NULL (default [])
    variables: z.unknown(), // jsonb NOT NULL (default {})
    is_active: z.boolean(),
    version: z.number(), // integer
    created_by: z.string().nullable(), // uuid NULLABLE
    created_at: wireTimestamp.nullable(), // timestamptz (default, nominalmente valorizzato)
    updated_at: wireTimestamp.nullable(),
    acts_as_user_ids: z.unknown().nullable(), // jsonb NULLABLE
    allowed_sender_ids: z.unknown().nullable(), // jsonb NULLABLE
    // Alias calcolati dalla SELECT
    created_by_name: z.string().nullable(), // CONCAT su LEFT JOIN users → null se created_by null
    run_count: pgCount, // COUNT(*) subquery senza ::int → stringa sul wire
    last_run_at: wireTimestamp.nullable(), // MAX(started_at) → null se nessun run
    // COALESCE(json_agg(...), '[]') → sempre array (mai null), oggetti {id, name}.
    linked_campaigns: z.array(z.object({ id: z.string(), name: z.string() })),
  })
  .merge(workflowDefinitionStateSchema)
  .extend({ campaign_selection: workflowCatalogSelectionSchema.optional() })
  .passthrough();
export type WorkflowListItem = z.infer<typeof workflowListItemSchema>;

export const workflowsListResponseSchema = z.object({
  workflows: z.array(workflowListItemSchema),
  total: pgCount, // COUNT(*) senza ::int → stringa sul wire
  limit: z.number(), // JS number
  offset: z.number(), // JS number
});
export type WorkflowsListResponse = z.infer<typeof workflowsListResponseSchema>;

// ----------------------------------------------------------------------------
// 6) GET /crm/pipeline-stages/protection-info → BARE (no success/data wrapper)
//    crm-pipeline.routes.ts:26-54.
// ----------------------------------------------------------------------------
export const crmPipelineStageProtectionInfoSchema = z.object({
  // typeCounts[stage_type] = r.count copiato senza Number() → COUNT pg = stringa sul wire.
  typeCounts: z.record(z.string(), pgCount),
  // campaignUsage[stage_id] = array di campaign_name.
  campaignUsage: z.record(z.string(), z.array(z.string())),
  // requiredTypes: costante ['entry','appointment','won','lost'].
  requiredTypes: z.array(z.string()),
});
export type CrmPipelineStageProtectionInfo = z.infer<typeof crmPipelineStageProtectionInfoSchema>;
